import { useEffect, useRef, useState } from "react";

type WakeLockSentinelLike = {
  released: boolean;
  release: () => Promise<void>;
  addEventListener: (type: "release", cb: () => void) => void;
};

type NavigatorWithWakeLock = Navigator & {
  wakeLock?: {
    request: (type: "screen") => Promise<WakeLockSentinelLike>;
  };
};

export const isWakeLockSupported = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return !!(navigator as NavigatorWithWakeLock).wakeLock;
};

/**
 * Acquire a screen wake lock while `enabled` is true and the page is visible.
 * Re-acquires automatically after the tab becomes visible again. No-ops on
 * browsers without Wake Lock API support.
 */
export function useWakeLock(enabled: boolean): { active: boolean; supported: boolean } {
  const [active, setActive] = useState(false);
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);
  const supported = isWakeLockSupported();

  useEffect(() => {
    if (!enabled || !supported) {
      setActive(false);
      return;
    }

    let cancelled = false;

    const acquire = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const nav = navigator as NavigatorWithWakeLock;
        const sentinel = await nav.wakeLock!.request("screen");
        if (cancelled) {
          await sentinel.release().catch(() => {});
          return;
        }
        sentinelRef.current = sentinel;
        setActive(true);
        sentinel.addEventListener("release", () => {
          if (sentinelRef.current === sentinel) {
            sentinelRef.current = null;
            setActive(false);
          }
        });
      } catch {
        setActive(false);
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !sentinelRef.current) {
        acquire();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      const s = sentinelRef.current;
      sentinelRef.current = null;
      setActive(false);
      if (s && !s.released) s.release().catch(() => {});
    };
  }, [enabled, supported]);

  return { active, supported };
}
